import React, { useEffect, useState } from 'react';
import PNTrading from 'src/pn-trading-sdk';
import usePopup from '../../utils/hooks/usePopup';
import Alert from '../../layout/popups/Alert';
import MediaLibrary from '../../layout/popups/MediaLibrary';

const WEBSITE_URL = 'https://riksauktioner.se';
const LOGO = 'https://content.riksauktioner.se/assets/images/riksauktioner-logo.png';
const APP_BANNER = 'https://content.riksauktioner.se/assets/images/RA_newsletter_banner_appstore_black1.png';
const APP_STORE_URL = 'https://apps.apple.com/se/app/riksauktioner/id1576332692';
const DEFAULT_HERO_GIF = 'https://riksauktioner.s3.eu-north-1.amazonaws.com/2025/10/RA_hemsida_hero_1+%2810%29.gif';

const FONT = 'Arial, Helvetica, sans-serif';

const escapeAttr = (str) => (str || '').replace(/"/g, '&quot;');
const escapeHtml = (str) => (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const imageSize = (image, size) => image?.sizes?.[size] || image?.sizes?.['500x500'] || image?.sizes?.['150x150'];

const DAYS = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];
const MONTHS_LC = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const pad = (n) => (n < 10 ? '0' : '') + n;
const timeString = (d) => `${d.getHours()}:${pad(d.getMinutes())}`;
const dayDateString = (d) => `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS_LC[d.getMonth()]}`;
const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const endingEyebrow = (d) => (isSameDay(d, new Date()) ? `I dag kl. ${timeString(d)}` : `${dayDateString(d)} kl. ${timeString(d)}`);
const metaOf = (auction, key) => {
	let m = auction?.data?.meta?.find((m) => m.key == key);
	return m ? m.value : undefined;
};

export default function MailTemplate() {
	const [open] = usePopup();

	const [data, setData] = useState(undefined);
	const [date, setDate] = useState(new Date().toJSON().substring(0, 10));
	const [description, setDescription] = useState('Från kl. 13.00 börjar veckans auktioner att avslutas. Följ dina favoriter live och lägg dina sista bud i god tid.');
	const [heroPill, setHeroPill] = useState('Auktionsmåndag');
	const [heroTitle, setHeroTitle] = useState('I dag avgörs det');
	const [ctaLabel, setCtaLabel] = useState('SE ALLA AUKTIONER OCH LÄGG BUD');

	const [heroImage, setHeroImage] = useState();

	useEffect(() => {
		PNTrading.get(`/website/weekly-mail?date=${date}`).then(setData);
	}, [date]);

	const copy = () => {
		let el = document.getElementById('pn-mail-template');

		const html = `<!DOCTYPE html>
<html lang="sv" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
	<meta charset="utf-8">
	<meta name="viewport" content="width=device-width,initial-scale=1">
	<meta http-equiv="X-UA-Compatible" content="IE=edge">
	<meta name="color-scheme" content="light only">
	<meta name="supported-color-schemes" content="light only">
	<title>Auktionsmåndag – veckans auktioner avslutas idag</title>
	<!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
	<style>
		body,table,td,a{-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;}
		table,td{mso-table-lspace:0pt;mso-table-rspace:0pt;border-collapse:separate;}
		img{-ms-interpolation-mode:bicubic;border:0;outline:none;text-decoration:none;}
		body{margin:0!important;padding:0!important;width:100%!important;background:#f2f2f0;}
		@media only screen and (max-width:620px){
			.container{width:100%!important;border-radius:0!important;}
			.outer{padding:0!important;}
			.px{padding-left:22px!important;padding-right:22px!important;}
			.hero-title{font-size:31px!important;line-height:37px!important;}
			.stack{display:block!important;width:100%!important;max-width:100%!important;padding-left:0!important;padding-right:0!important;}
			.card-image{width:118px!important;}
			.card-image img{width:118px!important;height:158px!important;}
			.card-copy{padding:14px!important;}
			.hide-mobile{display:none!important;}
		}
	</style>
</head>
<body style="margin:0;padding:0;background:#f2f2f0;font-family:${FONT};">
${el.innerHTML}
</body>
</html>`;

		navigator.clipboard.writeText(html);
		open(<Alert title="Kopierat mall" text={`Mallen har kopierats och kan användas för att skicka ett mail till kunder.`} />);
	};

	const media = () => {
		open(
			<MediaLibrary
				defaultImage={heroImage?.id}
				onChange={async (image) => {
					setHeroImage(image);
				}}
			/>
		);
	};

	const urlSuffix = `utm_source=newsletter&utm_medium=email&utm_campaign=weekly_mail_${new Date().toJSON().substring(0, 10)}`;

	if (!data)
		return (
			<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<div className="loader"></div>
			</div>
		);

	const allAuctionsUrl = `${WEBSITE_URL}/alla-objekt?${urlSuffix}`;
	const heroSrc = heroImage ? heroImage.sizes['1920'] : DEFAULT_HERO_GIF;

	// --- "Avslutas i dag" card (image left, copy + CTA right) ---
	const renderTodayCard = (auction) => {
		const url = `${WEBSITE_URL}/alla-objekt?auction=${auction.id}&${urlSuffix}`;
		const img = imageSize(auction.embed?.image, '500x500');
		const address = metaOf(auction, 'Adress');
		return `
			<tr>
				<td class="px" style="padding:0 40px 16px 40px;">
					<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e8e8e5;border-radius:12px;overflow:hidden;background:#ffffff;">
						<tr>
							<td class="card-image" width="168" style="width:168px;vertical-align:middle;line-height:0;">
								<a href="${url}" target="_blank">${img ? `<img src="${img}" width="168" alt="${escapeAttr(auction.name)}" style="display:block;width:168px;height:168px;object-fit:cover;">` : ''}</a>
							</td>
							<td class="card-copy" style="padding:18px 20px;vertical-align:middle;font-family:${FONT};">
								<div style="font-size:11px;line-height:15px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:#8a7300;margin-bottom:7px;">${endingEyebrow(new Date(auction.ending))}</div>
								<a href="${url}" target="_blank" style="text-decoration:none;color:#111111;"><div style="font-size:16px;line-height:22px;font-weight:600;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;max-height:44px;">${auction.name}</div></a>
								${address ? `<div style="font-size:13px;line-height:19px;color:#777777;margin-top:8px;">📍 ${address}</div>` : ''}
								<div style="margin-top:12px;">
									<a href="${url}" target="_blank" style="display:inline-block;background:#ffdf5b;color:#111111;text-decoration:none;font-size:12px;line-height:16px;font-weight:600;padding:9px 15px;border-radius:7px;">SE AUKTIONEN</a>
								</div>
							</td>
						</tr>
					</table>
				</td>
			</tr>`;
	};

	// --- "Populära objekt" grid cell (image top, copy below) ---
	const renderPopularCell = (object) => {
		const url = `${WEBSITE_URL}/objekt/${object.id}?${urlSuffix}`;
		const img = imageSize(object.embed?.thumbnail, '500x500');
		return `
			<td class="stack" width="50%" style="width:50%;vertical-align:top;padding:0 8px 16px 8px;">
				<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e8e8e5;border-radius:12px;overflow:hidden;background:#ffffff;">
					<tr><td style="line-height:0;">
						<a href="${url}" target="_blank">${img ? `<img src="${img}" width="244" alt="${escapeAttr(object.title)}" style="display:block;width:100%;height:190px;object-fit:cover;">` : ''}</a>
					</td></tr>
					<tr><td style="padding:15px 16px 17px 16px;font-family:${FONT};">
						<div style="font-size:11px;line-height:15px;font-weight:600;letter-spacing:.7px;text-transform:uppercase;color:#8a7300;">${endingEyebrow(new Date(object.ending))}</div>
						<a href="${url}" target="_blank" style="text-decoration:none;color:#111111;"><div style="font-size:14px;line-height:20px;font-weight:600;margin-top:7px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;height:40px;">${object.title}</div></a>
					</td></tr>
				</table>
			</td>`;
	};

	// --- "Fler auktioner ute nu" compact list row ---
	const renderOtherRow = (auction) => {
		const url = `${WEBSITE_URL}/alla-objekt?auction=${auction.id}&${urlSuffix}`;
		const img = imageSize(auction.embed?.image, '150x150');
		const address = metaOf(auction, 'Adress');
		return `
			<tr>
				<td style="padding:0 0 12px 0;">
					<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f7f7f5;border-radius:10px;">
						<tr>
							<td width="82" style="width:82px;vertical-align:middle;line-height:0;">
								<a href="${url}" target="_blank">${img ? `<img src="${img}" width="82" height="82" alt="" style="display:block;width:82px;height:82px;object-fit:cover;border-radius:10px 0 0 10px;">` : ''}</a>
							</td>
							<td style="padding:11px 14px;font-family:${FONT};vertical-align:middle;">
								<a href="${url}" target="_blank" style="text-decoration:none;color:#111111;"><div style="font-size:14px;line-height:19px;font-weight:600;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;max-height:38px;">${auction.name}</div></a>
								<div style="font-size:11px;line-height:16px;color:#777777;margin-top:5px;">${dayDateString(new Date(auction.ending))} kl. ${timeString(new Date(auction.ending))}${address ? `  •  ${address}` : ''}</div>
							</td>
							<td width="36" style="width:36px;padding-right:8px;font-family:${FONT};font-size:22px;color:#111111;text-align:center;">›</td>
						</tr>
					</table>
				</td>
			</tr>`;
	};

	const popularItems = (data.objects || []).filter((_, i) => i < 6);
	let popularRows = '';
	for (let i = 0; i < popularItems.length; i += 2) {
		const left = renderPopularCell(popularItems[i]);
		const right = popularItems[i + 1] ? renderPopularCell(popularItems[i + 1]) : `<td class="stack" width="50%" style="width:50%;padding:0 8px 16px 8px;">&nbsp;</td>`;
		popularRows += `<tr>${left}${right}</tr>`;
	}

	const sortedOtherAuctions = [...data.otherAuctions].sort((a, b) => new Date(a.ending).getTime() - new Date(b.ending).getTime());

	return (
		<>
			<div className="card" style={{ margin: 'auto', marginBottom: 70, marginTop: 30, width: 650 }}>
				<h2>Mejlmall</h2>
				<p>
					Använd denna mall för att kopiera HTML kod inför utskick av vecko-mejlet. <br /> OBS! Använd knappen "Kopiera HTML" för att få den genererade HTML koden.
				</p>
				<div className="spacer s"></div>
				<input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
				<div className="spacer s"></div>
				<label>Etikett (liten text ovanför rubriken)</label>
				<input type="text" value={heroPill} onChange={(e) => setHeroPill(e.target.value)} style={{ width: '100%' }} />
				<div className="spacer s"></div>
				<label>Rubrik</label>
				<input type="text" value={heroTitle} onChange={(e) => setHeroTitle(e.target.value)} style={{ width: '100%' }} />
				<div className="spacer s"></div>
				<label>Ingress</label>
				<textarea onChange={(e) => setDescription(e.target.value)} style={{ width: '100%', height: 150, resize: 'none' }} value={description} />
				<div className="spacer s"></div>
				<label>Knapptext</label>
				<input type="text" value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} style={{ width: '100%' }} />
				<div className="spacer s"></div>
				<button onClick={media}>Ändra hero bild</button>
				<div className="spacer s"></div>
				<button onClick={copy}>Kopiera HTML</button>
			</div>

			<div
				className="a4-page"
				id="pn-mail-template"
				dangerouslySetInnerHTML={{
					__html: `
	<table role="presentation" width="100%" cellpadding="0" cellspacing="0" bgcolor="#f2f2f0" style="background:#f2f2f0;font-family:${FONT};">
		<tr><td class="outer" align="center" style="padding:24px 12px;">
			<table role="presentation" class="container" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:600px;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 1px 5px rgba(0,0,0,.06);">

				<!-- Logo -->
				<tr><td align="center" style="padding:27px 32px 21px 32px;">
					<a href="${allAuctionsUrl}" target="_blank"><img src="${LOGO}" width="184" alt="Riksauktioner" style="display:block;width:184px;max-width:184px;height:auto;"></a>
				</td></tr>

				<!-- Hero image -->
				<tr><td style="line-height:0;"><img src="${heroSrc}" width="600" alt="Riksauktioner – auktion online" style="display:block;width:100%;max-width:600px;height:auto;"></td></tr>

				<!-- Dark hero -->
				<tr><td bgcolor="#111111" class="px" style="padding:34px 40px 38px 40px;font-family:${FONT};color:#ffffff;">
					<table role="presentation" cellpadding="0" cellspacing="0"><tr><td bgcolor="#ffdf5b" style="padding:7px 14px;border-radius:100px;font-size:11px;line-height:14px;font-weight:600;letter-spacing:1.2px;text-transform:uppercase;color:#111111;">${escapeHtml(heroPill)}</td></tr></table>
					<h1 class="hero-title" style="margin:19px 0 0 0;font-size:34px;line-height:40px;letter-spacing:-.8px;font-weight:600;color:#ffffff;">${escapeHtml(heroTitle)}</h1>
					<p style="margin:13px 0 0 0;font-size:17px;line-height:26px;color:#d4d4d4;">${description}</p>
					<table role="presentation" cellpadding="0" cellspacing="0" style="margin-top:25px;"><tr><td bgcolor="#ffdf5b" style="border-radius:8px;">
						<a href="${allAuctionsUrl}" target="_blank" style="display:inline-block;padding:14px 22px;color:#111111;text-decoration:none;font-size:14px;line-height:18px;font-weight:600;">${escapeHtml(ctaLabel)} →</a>
					</td></tr></table>
				</td></tr>

				<!-- Bra att veta -->
				<tr><td class="px" style="padding:28px 40px 8px 40px;">
					<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f7f7f5;border:1px solid #e8e8e5;border-radius:12px;">
						<tr><td style="padding:20px 21px;font-family:${FONT};">
							<div style="font-size:12px;line-height:16px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:#777777;margin-bottom:9px;">Bra att veta inför avslutet</div>
							<div style="font-size:14px;line-height:24px;color:#3d3d3d;">
								<strong style="color:#111111;">•</strong>  Alla bud är bindande<br>
								<strong style="color:#111111;">•</strong>  Sena bud förlänger objektets sluttid automatiskt<br>
								<strong style="color:#111111;">•</strong>  Kontrollera att du är inloggad innan din auktion avslutas
							</div>
						</td></tr>
					</table>
				</td></tr>

				${data.auctionsEndingToday.length > 0 ? `
				<!-- Avslutas i dag -->
				<tr><td class="px" style="padding:28px 40px 19px 40px;font-family:${FONT};">
					<div style="font-size:12px;line-height:16px;font-weight:600;letter-spacing:1.2px;text-transform:uppercase;color:#8a7300;">${data.auctionsEndingToday.length} auktioner</div>
					<h2 style="margin:7px 0 0 0;font-size:27px;line-height:34px;font-weight:600;color:#111111;">Avslutas i dag</h2>
					<p style="margin:9px 0 0 0;font-size:14px;line-height:22px;color:#6b6b6b;">Följ avsluten live och lägg dina sista bud i god tid.</p>
				</td></tr>
				${data.auctionsEndingToday.map(renderTodayCard).join('')}
				<tr><td class="px" align="center" style="padding:8px 40px 38px 40px;">
					<a href="${allAuctionsUrl}" target="_blank" style="display:block;background:#111111;color:#ffffff;text-decoration:none;font-family:${FONT};font-size:14px;line-height:18px;font-weight:600;padding:15px 20px;border-radius:8px;">${escapeHtml(ctaLabel)}</a>
				</td></tr>` : ''}

				${popularItems.length > 0 ? `
				<!-- Populära objekt -->
				<tr><td bgcolor="#f7f7f5" class="px" style="padding:34px 32px 18px 32px;font-family:${FONT};">
					<div style="font-size:12px;line-height:16px;font-weight:600;letter-spacing:1.2px;text-transform:uppercase;color:#8a7300;padding:0 8px;">Mest intresse just nu</div>
					<h2 style="margin:7px 8px 20px 8px;font-size:27px;line-height:34px;font-weight:600;color:#111111;">Populära objekt</h2>
					<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${popularRows}</table>
				</td></tr>` : ''}

				<!-- Sälj via oss -->
				<tr><td bgcolor="#ffdf5b" class="px" style="padding:33px 40px 35px 40px;font-family:${FONT};color:#111111;">
					<div style="font-size:11px;line-height:15px;font-weight:600;letter-spacing:1.2px;text-transform:uppercase;">Sälj via oss</div>
					<h2 style="margin:8px 0 0 0;font-size:24px;line-height:31px;font-weight:600;">Att frigöra kapital är enklare än du tror</h2>
					<p style="margin:12px 0 0 0;font-size:15px;line-height:23px;">Har du inventarier, maskiner, fordon eller lager som binder kapital? Vi hjälper företag och privatpersoner att sälja – från enstaka objekt till hela verksamheter. Vi sköter hela processen från start till mål.</p>
					<table role="presentation" cellpadding="0" cellspacing="0" style="margin-top:21px;"><tr><td bgcolor="#111111" style="border-radius:8px;">
						<a href="mailto:uppdrag@riksauktioner.se" style="display:inline-block;padding:13px 20px;color:#ffffff;text-decoration:none;font-size:13px;line-height:17px;font-weight:600;">KONTAKTA OSS →</a>
					</td></tr></table>
				</td></tr>

				${sortedOtherAuctions.length > 0 ? `
				<!-- Fler auktioner ute nu -->
				<tr><td class="px" style="padding:36px 40px 20px 40px;font-family:${FONT};">
					<div style="font-size:12px;line-height:16px;font-weight:600;letter-spacing:1.2px;text-transform:uppercase;color:#8a7300;">Nästa chans</div>
					<h2 style="margin:7px 0 0 0;font-size:27px;line-height:34px;font-weight:600;color:#111111;">Fler auktioner ute nu</h2>
					<p style="margin:9px 0 22px 0;font-size:14px;line-height:22px;color:#6b6b6b;">Upptäck kommande auktioner och börja följa dina favoriter redan nu.</p>
					<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${sortedOtherAuctions.map(renderOtherRow).join('')}</table>
				</td></tr>` : ''}

				<!-- App banner -->
				<tr><td style="line-height:0;">
					<a href="${APP_STORE_URL}" target="_blank"><img src="${APP_BANNER}" width="600" alt="Hämta Riksauktioners app" style="display:block;width:100%;max-width:600px;height:auto;"></a>
				</td></tr>

				<!-- Support -->
				<tr><td bgcolor="#111111" class="px" style="padding:35px 40px 38px 40px;font-family:${FONT};color:#ffffff;text-align:center;">
					<h2 style="margin:0;font-size:23px;line-height:30px;font-weight:600;color:#ffffff;">Behöver du hjälp under avslutet?</h2>
					<p style="margin:11px 0 0 0;font-size:14px;line-height:22px;color:#d1d1d1;">Vår support finns tillgänglig under dagen om du har frågor om budgivningen eller något objekt.</p>
					<p style="margin:21px 0 0 0;font-size:14px;line-height:22px;color:#ffffff;font-weight:600;">Lycka till i budgivningen!</p>
					<p style="margin:7px 0 0 0;font-size:13px;line-height:20px;color:#bdbdbd;">Riksauktioner – där affärer möter möjligheter.</p>
				</td></tr>

				<!-- Unsubscribe -->
				<tr><td bgcolor="#424242" style="padding:15px 25px;font-family:${FONT};font-size:11px;line-height:17px;color:#d7d7d7;text-align:center;">
					<unsubscribe style="color:#d7d7d7;text-decoration:underline;cursor:pointer;">Skicka inga fler mejl till mig, tack!</unsubscribe>
				</td></tr>
			</table>
		</td></tr>
	</table>
				`,
				}}
			/>
		</>
	);
}
